import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Pagination, CircularProgress, TextField,
  InputAdornment, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Alert, Snackbar, IconButton, Grid, MenuItem,
} from '@mui/material';
import { Visibility, Replay, Add, Remove, WhatsApp, Email, PictureAsPdf } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';

const statusColors = { paid: 'success', partial: 'warning', credit: 'error' };
const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

function SaleDetailDialog({ saleId, open, onClose, onReturn }) {
  const [sale, setSale] = useState(null);

  useEffect(() => {
    if (open && saleId) api.get(`/sales/${saleId}`).then(r => setSale(r.data.data));
  }, [open, saleId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invoice — {sale?.invoice_number}</DialogTitle>
      <DialogContent>
        {!sale ? <CircularProgress /> : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Customer</Typography>
                <Typography fontWeight={600}>{sale.customer_name || 'Walk-in'}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography fontWeight={600}>{new Date(sale.created_at).toLocaleString('en-PK')}</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {sale.items?.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">₨ {fmt(item.unit_price)}</TableCell>
                    <TableCell align="right">₨ {fmt(item.total_price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography>₨ {fmt(sale.subtotal)}</Typography>
              </Box>
              {sale.discount_amount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Discount</Typography>
                  <Typography color="error.main">- ₨ {fmt(sale.discount_amount)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontWeight={700}>Total</Typography>
                <Typography fontWeight={700} color="primary">₨ {fmt(sale.total_amount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Paid</Typography>
                <Typography>₨ {fmt(sale.paid_amount)}</Typography>
              </Box>
              {sale.due_amount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="error.main" fontWeight={600}>Due</Typography>
                  <Typography color="error.main" fontWeight={600}>₨ {fmt(sale.due_amount)}</Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {/* Print / Download PDF */}
        <Button
          variant="contained"
          color="error"
          startIcon={<PictureAsPdf />}
          onClick={async () => {
            const auth = JSON.parse(localStorage.getItem('pos_auth') || '{}');
            const token = auth.accessToken;
            const res = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/export/pdf/invoice/${sale.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) { alert('PDF generation failed'); return; }
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            // Open in new tab — browser will show its own print dialog
            window.open(url, '_blank');
          }}
        >
          Print / PDF
        </Button>
        {sale?.customer_email && (
          <Button variant="outlined" color="info" startIcon={<Email />}
            onClick={async () => {
              try { await api.post(`/sales/${sale.id}/email-invoice`, { email: sale.customer_email }); alert('Invoice emailed!'); }
              catch (e) { alert(e.response?.data?.message || 'Email failed'); }
            }}>
            Email Invoice
          </Button>
        )}
        {sale?.customer_phone && (
          <Button variant="outlined" color="success" startIcon={<WhatsApp />}
            onClick={async () => {
              try { await api.post(`/whatsapp/send-invoice/${sale.id}`); }
              catch (e) { alert(e.response?.data?.message || 'WhatsApp send failed'); }
            }}>
            WhatsApp
          </Button>
        )}
        <Button variant="outlined" color="warning" startIcon={<Replay />} onClick={() => onReturn(sale)}>Return Items</Button>
      </DialogActions>
    </Dialog>
  );
}

function ReturnDialog({ sale, open, onClose, onSuccess }) {
  const [qtys, setQtys] = useState({});
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sale?.items) {
      const init = {};
      sale.items.forEach(i => { init[i.id] = 0; });
      setQtys(init); setError('');
    }
  }, [sale]);

  const handle = async () => {
    const items = Object.entries(qtys).filter(([, q]) => q > 0).map(([id, qty]) => ({ sale_item_id: +id, quantity: qty }));
    if (!items.length) { setError('Select at least one item to return.'); return; }
    setLoading(true);
    try {
      await api.post(`/sales/${sale.id}/return`, { items, reason });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Return failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Return Items — {sale?.invoice_number}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="center">Sold</TableCell>
            <TableCell align="center">Return Qty</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {sale?.items?.map(item => (
              <TableRow key={item.id}>
                <TableCell>{item.product_name}</TableCell>
                <TableCell align="center">{item.quantity}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <IconButton size="small" onClick={() => setQtys(q => ({ ...q, [item.id]: Math.max(0, (q[item.id]||0) - 1) }))}>
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{qtys[item.id] || 0}</Typography>
                    <IconButton size="small" onClick={() => setQtys(q => ({ ...q, [item.id]: Math.min(item.quantity, (q[item.id]||0) + 1) }))}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TextField fullWidth multiline rows={2} label="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={handle} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Process Return'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SalesPage() {
  const { t } = useTranslation();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [detailSale, setDetailSale] = useState(null);
  const [paySale, setPaySale] = useState(null);
  const [returnSale, setReturnSale] = useState(null);
  const [snack, setSnack] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get('/sales', { params: { page, limit: 15, from: from || undefined, to: to || undefined } });
    setSales(data.data); setTotal(data.pagination.total); setLoading(false);
  }, [page, from, to]);

  useEffect(() => { load(); }, [load]);

  const totalRevenue = sales.reduce((s, sale) => s + +sale.total_amount, 0);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>{t('nav.pos')} — Sales History</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="To" type="date" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="outlined" onClick={() => { setFrom(''); setTo(''); }}>Clear</Button>
            {sales.length > 0 && <Chip label={`Page total: ₨ ${fmt(totalRevenue)}`} color="primary" sx={{ ml: 'auto' }} />}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{t('sales.invoiceNo')}</TableCell>
                <TableCell>{t('sales.customer')}</TableCell>
                <TableCell>{t('sales.cashier')}</TableCell>
                <TableCell align="right">{t('sales.total')}</TableCell>
                <TableCell align="right">{t('sales.paid')}</TableCell>
                <TableCell align="right">{t('sales.due')}</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {sales.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={700} color="primary">{s.invoice_number}</Typography></TableCell>
                    <TableCell>{s.customer_name || 'Walk-in'}</TableCell>
                    <TableCell>{s.cashier_name}</TableCell>
                    <TableCell align="right">₨ {fmt(s.total_amount)}</TableCell>
                    <TableCell align="right">₨ {fmt(s.paid_amount)}</TableCell>
                    <TableCell align="right" sx={{ color: s.due_amount > 0 ? 'error.main' : 'inherit' }}>
                      {s.due_amount > 0 ? `₨ ${fmt(s.due_amount)}` : '—'}
                    </TableCell>
                    <TableCell><Chip label={s.payment_status} size="small" color={statusColors[s.payment_status] || 'default'} /></TableCell>
                    <TableCell><Typography variant="caption">{new Date(s.created_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" startIcon={<Visibility />} onClick={() => setDetailId(s.id)}>View</Button>
                        {(s.payment_status === 'partial' || s.payment_status === 'credit') && s.due_amount > 0 && (
                          <Button size="small" variant="contained" color="success" onClick={() => setPaySale(s)}>
                            Receive Payment
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      <SaleDetailDialog
        saleId={detailId} open={!!detailId}
        onClose={() => setDetailId(null)}
        onReturn={(sale) => { setDetailSale(sale); setDetailId(null); setReturnSale(sale); }}
      />
      <ReturnDialog
        sale={returnSale} open={!!returnSale}
        onClose={() => setReturnSale(null)}
        onSuccess={() => { setReturnSale(null); setSnack('Return processed successfully'); load(); }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
      {paySale && (
        <ReceivePaymentDialog
          sale={paySale}
          open={!!paySale}
          onClose={() => setPaySale(null)}
          onSuccess={() => { setPaySale(null); setSnack('Payment recorded successfully'); load(); }}
        />
      )}
    </Box>
  );
}

function ReceivePaymentDialog({ sale, open, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [payments, setPayments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && sale) {
      setAmount(sale.due_amount || '');
      setMethod('cash');
      setReference('');
      setErr('');
      api.get(`/sales/${sale.id}/payments`).then(r => setPayments(r.data.data || [])).catch(() => {});
    }
  }, [open, sale]);

  const submit = async () => {
    if (!amount || +amount <= 0) { setErr('Enter a valid amount'); return; }
    setSaving(true); setErr('');
    try {
      await api.post(`/sales/${sale.id}/receive-payment`, { amount: +amount, method, reference });
      onSuccess();
    } catch (e) { setErr(e.response?.data?.message || 'Failed to record payment'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Receive Payment — {sale?.invoice_number}
        <Typography variant="body2" color="text.secondary">
          Total: Rs.{fmt(sale?.total_amount)} | Paid: Rs.{fmt(sale?.paid_amount)} | <strong style={{color:'#ef4444'}}>Due: Rs.{fmt(sale?.due_amount)}</strong>
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Amount Receiving (Rs.)" type="number"
              value={amount} onChange={e => setAmount(e.target.value)}
              inputProps={{ min: 0.01, max: sale?.due_amount, step: 0.01 }} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Payment Method" value={method} onChange={e => setMethod(e.target.value)}>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer / JazzCash / EasyPaisa</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Reference / Transaction ID (optional)" value={reference} onChange={e => setReference(e.target.value)} />
          </Grid>
        </Grid>

        {payments.length > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Previous Payments</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Date</TableCell><TableCell>Amount</TableCell><TableCell>Method</TableCell><TableCell>By</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {payments.map(p => (
                  <TableRow key={p.id}>
                    <TableCell><Typography variant="caption">{new Date(p.paid_at).toLocaleString('en-PK')}</Typography></TableCell>
                    <TableCell><Typography color="success.main" fontWeight={600}>Rs.{fmt(p.amount)}</Typography></TableCell>
                    <TableCell>{p.method}</TableCell>
                    <TableCell>{p.received_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={submit} disabled={saving}>
          {saving ? 'Recording...' : `Record Rs.${fmt(amount || 0)} Payment`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
